import type { ProjectCostTelemetryResponse } from "@automatry/schemas";

import { cx } from "./shared";

const compactNumberFormatter = new Intl.NumberFormat("en-GB");

export function TokenUsageScreen({
  costRollup
}: {
  costRollup: ProjectCostTelemetryResponse | null | undefined;
}) {
  return (
    <div className="flex-1 overflow-auto px-5 py-6 sm:px-8 xl:px-14 xl:py-8">
      <div className="mx-auto max-w-[1120px]">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-[#2E445A] px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-[.08em] text-white">
            Internal
          </span>
          <span className="text-[10.5px] font-extrabold uppercase tracking-[.1em] text-[var(--faint)]">
            AI operations
          </span>
        </div>
        <h1 className="m-0 mt-3 text-[30px] font-extrabold tracking-[-.035em] text-[var(--ink)]">
          Token usage
        </h1>
        <p className="m-0 mt-2 max-w-[720px] text-[13px] font-semibold leading-[1.5] text-[var(--muted)]">
          Provider calls, recorded tokens and estimated internal cost for this project.
          This operational view is not part of the client controls specification.
        </p>
        <CostTelemetryPanel costRollup={costRollup} />
      </div>
    </div>
  );
}

export function CostTelemetryPanel({
  costRollup
}: {
  costRollup: ProjectCostTelemetryResponse | null | undefined;
}) {
  if (!costRollup) {
    return (
      <section
        aria-label="Estimated AI cost"
        className="mt-6 rounded-[12px] border border-dashed border-[var(--line)] bg-[var(--surface)] px-5 py-6"
      >
        <div className="text-[11px] font-extrabold uppercase tracking-[.08em] text-[var(--faint)]">
          AI cost estimate
        </div>
        <p className="m-0 mt-2 text-[13px] font-semibold text-[var(--muted)]">
          No token-usage telemetry has been recorded for this project yet.
        </p>
      </section>
    );
  }

  const budget = costRollup.budgets[0];
  const operations = costRollup.operations.filter(
    (operation) => operation.callCount > 0
  );
  const callClasses = costRollup.callClasses.filter(
    (callClass) => callClass.callCount > 0
  );

  return (
    <section
      aria-label="Estimated AI cost"
      className="mt-6 rounded-[12px] border border-[var(--line)] bg-[var(--raised)] px-4 py-4 text-[var(--ink2)] sm:px-5 sm:py-5"
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="text-[10.5px] font-extrabold uppercase tracking-[.08em] text-[var(--info)]">
            AI cost estimate
          </div>
          <div className="mt-1 text-[24px] font-extrabold tracking-[-.03em] text-[var(--ink)]">
            {costRollup.total.estimateText}
          </div>
          <div className="mt-1 text-[12px] font-semibold leading-[1.45] text-[var(--muted2)]">
            {compactNumberFormatter.format(costRollup.total.totalTokens)} recorded
            tokens across {formatCallCount(costRollup.total.callCount)}. Estimate only;
            final provider billing may differ. {costRollup.reconciliation.message}
          </div>
        </div>
        {budget ? (
          <div
            className={cx(
              "rounded-[10px] px-3.5 py-2.5 text-[11.5px] font-bold leading-[1.4] shadow-[inset_0_0_0_1px_rgba(0,0,0,.08)] lg:min-w-[240px]",
              budget.status === "exhausted"
                ? "bg-[#F6E7E4] text-[#9F4238]"
                : "bg-[#EAF1E6] text-[#4E7644]"
            )}
          >
            <div>{budget.label}</div>
            <div className="mt-0.5 tabular-nums">
              {budget.consumedGbp.formatted} used of {budget.limitGbp.formatted};{" "}
              {budget.remainingGbp.formatted} remaining
            </div>
          </div>
        ) : null}
      </div>
      {operations.length > 0 ? (
        <div className="mt-4 overflow-hidden rounded-[10px] bg-[var(--surface)] shadow-[inset_0_0_0_1px_var(--line)]">
          {operations.map((operation) => (
            <div
              key={operation.operationId}
              className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border-t border-[var(--line)] px-3.5 py-2.5 text-[11.5px] first:border-t-0"
            >
              <div className="min-w-[150px] flex-1 font-extrabold text-[var(--ink)]">
                {operation.label}
              </div>
              <div className="tabular-nums text-[var(--muted)]">
                {formatCallCount(operation.callCount)}
              </div>
              <div className="tabular-nums text-[var(--muted)]">
                {formatTokenCount(operation.totalTokens)}
              </div>
              <div className="ml-auto font-extrabold text-[var(--ink)]">
                {operation.estimateText}
                <span className="ml-1 font-semibold text-[var(--faint)]">
                  ({operation.estimatedUsd.formatted} estimated USD)
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-4 rounded-[10px] border border-dashed border-[var(--line)] bg-[var(--surface)] px-3.5 py-4 text-[11.5px] font-bold text-[var(--muted2)]">
          No AI operations yet
        </div>
      )}
      {callClasses.length > 0 ? (
        <div className="mt-4 overflow-hidden rounded-[10px] bg-[var(--surface)] shadow-[inset_0_0_0_1px_var(--line)]">
          {callClasses.map((callClass) => (
            <div
              key={callClass.callClass}
              className="border-t border-[var(--line)] px-3.5 py-2.5 first:border-t-0"
            >
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-[11.5px]">
                <div className="min-w-[170px] flex-1 font-extrabold text-[var(--ink)]">
                  {callClass.label}
                </div>
                <div className="tabular-nums text-[var(--muted)]">
                  {formatCallCount(callClass.callCount)}
                </div>
                <div className="tabular-nums text-[var(--muted)]">
                  {formatTokenCount(callClass.totalTokens)}
                </div>
                <div className="ml-auto font-extrabold text-[var(--ink)]">
                  {callClass.estimateText}
                </div>
              </div>
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[10.5px] font-semibold text-[var(--muted2)]">
                {callClass.providers.map((provider) => (
                  <span key={`${provider.provider}:${provider.modelId}`}>
                    {provider.provider} / {provider.modelId}:{" "}
                    {formatTokenCount(provider.totalTokens)},{" "}
                    {provider.estimatedUsd.formatted} estimated USD
                    {provider.missingUsageCount > 0
                      ? `; ${provider.missingUsageCount.toString()} missing usage`
                      : ""}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : null}
      <div className="mt-3 text-[10.5px] font-semibold leading-[1.4] text-[var(--muted2)]">
        {costRollup.estimateDisclaimer} Pricing basis: 1 GBP ={" "}
        {formatGbpToUsdRate(costRollup.pricingBasis.gbpToUsdRate)} USD.
      </div>
    </section>
  );
}

function formatTokenCount(value: number): string {
  return `${compactNumberFormatter.format(value)} token${value === 1 ? "" : "s"}`;
}

function formatCallCount(value: number): string {
  return `${compactNumberFormatter.format(value)} call${value === 1 ? "" : "s"}`;
}

function formatGbpToUsdRate(value: number): string {
  return value.toFixed(2);
}
